import { FormikHelpers, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import React, { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { object, string } from 'yup';

import Botao from 'Component/Botao';
import Buscando from 'Component/Buscando';
import Cabecalho from 'Component/Cabecalho';
import Form, { Campo } from 'Component/Form';
import LayoutDefault from 'Component/Layout';
import { useRecuperarFechamentosDeFolhaQuery } from 'View/Service/Default/FechamentosDeFolha';

const regras = object({
  inicio: string().required('Obrigatório'),
  termino: string().required('Obrigatório'),
  busca: string().optional(),
});

const Relatorios = (): React.ReactElement => {
  const ref = useRef<HTMLTableElement>();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: { fechamentos_de_folha } = {},
    isFetching,
    isLoading,
    refetch,
  } = useRecuperarFechamentosDeFolhaQuery({
    contain: [
      'VinculosEmpregaticios.Usuarios',
      'VinculosEmpregaticios.Empresas',
    ],
    limit: 200,
    options: {
      busca: { termo: searchParams.get('busca') || '' },
      inicio:
        searchParams.get('inicio') ||
        DateTime.now().set({ day: 1 }).toFormat('yyyy-MM-dd'),
      termino:
        searchParams.get('termino') || DateTime.now().toFormat('yyyy-MM-dd'),
      status: 1,
    },
  });

  const filtrar = (
    dados: { busca: string },
    form: FormikHelpers<FormikValues>
  ) => {
    if (dados.inicio) {
      searchParams.set('inicio', dados.inicio);
    } else {
      searchParams.delete('inicio');
    }
    if (dados.termino) {
      searchParams.set('termino', dados.termino);
    } else {
      searchParams.delete('termino');
    }
    searchParams.set('busca', dados.busca);
    searchParams.set('pagina', '1');

    setSearchParams(searchParams);

    form.setSubmitting(false);
  };

  const downloadCsv = () => {
    if (ref.current) {
      const rows = ref.current.querySelectorAll('tr');

      const csv = [];
      for (let i = 0; i < rows.length; i++) {
        const row = [],
          cols = rows[i].querySelectorAll('td, th');
        for (let j = 0; j < cols.length; j++) {
          let data = cols[j].innerHTML
            .replace(/(\r\n|\n|\r)/gm, '')
            .replace(/(\s\s)/gm, ' ');
          data = data.replace(/"/g, '""');
          row.push('"' + data + '"');
        }
        csv.push(row.join(','));
      }
      const csv_string = csv.join('\n');

      const filename = 'relatorio_' + new Date().toLocaleDateString() + '.csv';
      const link = document.createElement('a');
      link.style.display = 'none';
      link.setAttribute('target', '_blank');
      link.setAttribute(
        'href',
        'data:text/csv;charset=utf-8,' + encodeURIComponent(csv_string)
      );
      link.setAttribute('download', filename);
      link.click();
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Relatório de Fechamento de folha"
        descricao="Aqui você encontra o relatório de fechamento de folha com exportação"
      />
      <Form
        emLinha
        onSubmit={filtrar}
        regras={regras}
        desabilitado={isFetching}
      >
        <div className="row">
          <div className="col-md-1 mb-3">
            <Botao icone="arrow-clockwise" onClick={refetch} className="me-2" />
          </div>
          <div className="col-md-11 mb-3">
            <div className="hstack gap-3 flex-column flex-sm-row align-items-start">
              <Campo name="inicio" rotulo="Data inicial" type="date" />
              <Campo name="termino" rotulo="Data inicial" type="date" />
              <Campo name="busca" rotulo="Digite o que deseja buscar" />
              <Botao type="submit" icone="filter" />
              <Botao
                tema="info"
                icone="filetype-csv"
                onClick={() => downloadCsv()}
              />
            </div>
          </div>
        </div>
      </Form>
      <Buscando exibir={isLoading} />
      <React.Fragment>
        <div className="bg-white shadow-sm rounded">
          <div className="table-responsive text-nowrap">
            <table className="table table-striped mb-0" ref={ref}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nome</th>
                  <th>Empresa</th>
                  <th>Salário</th>
                  <th>Comissão</th>
                  <th>Ajuda de custo</th>
                  <th>Prêmio de metas - Vendedores</th>
                  <th>Prêmio de metas - Geral</th>
                  <th>Vale transporte</th>
                  <th>Adiantamento</th>
                  <th>Con. Farmácia</th>
                  <th>Con. Médico</th>
                  <th>Con. Odonto</th>
                  <th>Consignado</th>
                  <th>Outros descontos</th>
                  <th>Observações</th>
                  <th>Período inicial</th>
                  <th>Período final</th>
                  <th>Criado em</th>
                </tr>
              </thead>
              <tbody>
                {fechamentos_de_folha?.map((folha) => (
                  <tr key={`${folha.id}-${folha.modificado}`}>
                    <td>{folha.id}</td>
                    <td>{folha.vinculo_empregaticio?.usuario?.nome}</td>
                    <td>
                      {folha.vinculo_empregaticio?.empresa?.nome_fantasia}
                    </td>
                    <td>{folha.salario?.moeda()}</td>
                    <td>{folha.valor?.moeda()}</td>
                    <td>{folha.ajuda_de_custo?.moeda()}</td>
                    <td>{folha.premio_de_metas_vendedores?.moeda()}</td>
                    <td>{folha.premio_de_metas_geral?.moeda()}</td>
                    <td>{folha.vale_transporte?.moeda()}</td>
                    <td>{folha.adiantamento?.moeda()}</td>
                    <td>{folha.farmacia?.moeda()}</td>
                    <td>{folha.medico?.moeda()}</td>
                    <td>{folha.odonto?.moeda()}</td>
                    <td>{folha.consignado?.moeda()}</td>
                    <td>{folha.descontos?.moeda()}</td>
                    <td>{folha.observacoes}</td>
                    <td>{folha.inicio.data().toLocaleString()}</td>
                    <td>{folha.termino.data().toLocaleString()}</td>
                    <td>
                      {folha.criado
                        .data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </React.Fragment>
    </LayoutDefault>
  );
};

export default Relatorios;
